import { API_ERROR_KIND, classifyApiError, extractApiError } from '../../utils/errors';

/**
 * Every way an operator API call can fail, as the screens act on it.
 *
 * The API says more than a sentence: which companies' module is off
 * (`reason`), which product an ambiguous code could be (`candidates`), what a
 * receipt would close short of (`missing`), what a count would go over
 * (`excess`). `extractApiErrorDetail` flattens those into strings, so this
 * reads `response.data` itself.
 */
export const OPERATOR_ERROR = {
  /** Nothing answered: a dead Wi-Fi, a timeout. Retrying the same key is safe. */
  NO_RESPONSE: 'no_response',
  /** 400: a field, or a warehouse rule (stock, the order's state, the slot). */
  REFUSED: 'refused',
  /** 403 with a `reason`: the module or the flow is off. */
  MODULE_INACTIVE: 'module_inactive',
  /** 401/403 from DRF itself: not signed in, or not an operator. */
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'not_found',
  /** 409 with `candidates`: the code is more than one thing; pick one and resend. */
  AMBIGUOUS: 'ambiguous',
  /**
   * 409 with `missing` (closing short) or `excess` (counting over what a line
   * lacks): nothing was done, and it goes again only once confirmed — a new
   * action, not a retry.
   */
  CONFIRM: 'confirm',
  /** 409 alone: the database aborted on a clash. Retrying the same key is safe. */
  CONFLICT: 'conflict',
  /** 412: the screen is older than the server's state; reload it before anything else. */
  STALE: 'stale',
  SERVER: 'server',
  /** The code does not belong to the step; no request was made. */
  LOCAL: 'local',
};

const NO_RESPONSE_MESSAGE = 'Sin respuesta del servidor.';

/**
 * @param {unknown} error - An axios rejection, or anything a thunk threw.
 * @param {string} [fallback]
 * @returns {{kind: string, status: number|null, message: string, reason?: string,
 *   candidates?: Array<object>, missing?: Array<object>, excess?: Array<object>, fieldErrors?: object|null}}
 */
export const readOperatorError = (error, fallback = 'No se pudo completar la acción.') => {
  const { kind, status } = classifyApiError(error);
  const data = error?.response?.data;
  const body = data && typeof data === 'object' && !Array.isArray(data) ? data : {};

  if (kind === API_ERROR_KIND.UNREACHABLE || kind === API_ERROR_KIND.TIMEOUT) {
    return { kind: OPERATOR_ERROR.NO_RESPONSE, status, message: NO_RESPONSE_MESSAGE };
  }

  const message = extractApiError(error, fallback);

  if (status === 409) {
    if (Array.isArray(body.candidates) && body.candidates.length > 0) {
      return { kind: OPERATOR_ERROR.AMBIGUOUS, status, message, candidates: body.candidates };
    }
    if (Array.isArray(body.missing)) {
      return { kind: OPERATOR_ERROR.CONFIRM, status, message, missing: body.missing };
    }
    if (Array.isArray(body.excess)) {
      return { kind: OPERATOR_ERROR.CONFIRM, status, message, excess: body.excess };
    }
    return { kind: OPERATOR_ERROR.CONFLICT, status, message };
  }
  if (status === 412) return { kind: OPERATOR_ERROR.STALE, status, message };
  if (status === 403 && typeof body.reason === 'string') {
    return { kind: OPERATOR_ERROR.MODULE_INACTIVE, status, message, reason: body.reason };
  }
  if (status === 401 || kind === API_ERROR_KIND.FORBIDDEN) {
    return { kind: OPERATOR_ERROR.FORBIDDEN, status, message };
  }
  if (kind === API_ERROR_KIND.NOT_FOUND) return { kind: OPERATOR_ERROR.NOT_FOUND, status, message };
  if (kind === API_ERROR_KIND.SERVER) return { kind: OPERATOR_ERROR.SERVER, status, message };

  return { kind: OPERATOR_ERROR.REFUSED, status, message, fieldErrors: body.errors ?? null };
};

/**
 * A failure for something that was never sent: a code that is not what the
 * step asked for.
 *
 * @param {string} message
 */
export const localError = (message) => ({ kind: OPERATOR_ERROR.LOCAL, status: null, message });

/** Whether sending the very same request again — same idempotency key — is the fix. */
export const isRetryable = (error) =>
  [OPERATOR_ERROR.NO_RESPONSE, OPERATOR_ERROR.CONFLICT, OPERATOR_ERROR.SERVER].includes(error?.kind);
